import React, { useState, useEffect, useRef } from 'react';
import { useEditor, EditorContent, Node, mergeAttributes } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { TableKit } from '@tiptap/extension-table';
import {
  Pilcrow, Heading1, Heading2, Heading3, Bold, Italic, Underline as UnderlineIcon, List, ListOrdered,
  Quote, Link2, Minus, Undo2, Redo2, Eraser, Check, X, MessageSquareQuote, ImagePlus, Loader2,
} from 'lucide-react';
import * as api from '../lib/api';
import { prepareImage } from '../lib/imageResize';

/** Lessons written before the rich editor are stored as simple markdown. Turn that into HTML so they open correctly. */
const escapeHtml = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const inline = (s) => escapeHtml(s).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').replace(/\*(.+?)\*/g, '<em>$1</em>');
export const legacyToHtml = (text) => {
  if (!text || text.trim().startsWith('<')) return text || '';
  const out = [];
  let list = null;
  const close = () => { if (list) { out.push(`</${list}>`); list = null; } };
  text.split('\n').forEach((raw) => {
    const line = raw.trimEnd();
    const bullet = line.match(/^[-*]\s+(.*)/);
    const numbered = line.match(/^\d+\.\s+(.*)/);
    if (bullet || numbered) {
      const kind = bullet ? 'ul' : 'ol';
      if (list !== kind) { close(); out.push(`<${kind}>`); list = kind; }
      out.push(`<li><p>${inline((bullet || numbered)[1])}</p></li>`);
      return;
    }
    close();
    if (line.startsWith('### ')) out.push(`<h3>${inline(line.slice(4))}</h3>`);
    else if (line.startsWith('## ')) out.push(`<h2>${inline(line.slice(3))}</h2>`);
    else if (line.startsWith('# ')) out.push(`<h1>${inline(line.slice(2))}</h1>`);
    else if (line.trim()) out.push(`<p>${inline(line)}</p>`);
  });
  close();
  return out.join('');
};


/** A boxed note ("A note from me", "A gentle check-in"). Stored as <div data-callout data-label="...">. */
const Callout = Node.create({
  name: 'callout',
  group: 'block',
  content: 'block+',
  defining: true,
  addAttributes() {
    return {
      label: {
        default: 'Reflection',
        parseHTML: (el) => el.getAttribute('data-label') || 'Reflection',
        renderHTML: (attrs) => ({ 'data-label': attrs.label }),
      },
    };
  },
  parseHTML() { return [{ tag: 'div[data-callout]' }]; },
  renderHTML({ HTMLAttributes }) { return ['div', mergeAttributes(HTMLAttributes, { 'data-callout': '' }), 0]; },
  addCommands() {
    return {
      toggleCallout: (label) => ({ commands, editor }) => (editor.isActive('callout') ? commands.lift('callout') : commands.wrapIn('callout', { label: label || 'Reflection' })),
      setCalloutLabel: (label) => ({ commands }) => commands.updateAttributes('callout', { label }),
    };
  },
});

/** An image placed inside the text, where the lesson copy wants it. */
const InlineImage = Node.create({
  name: 'image',
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes() { return { src: { default: null }, alt: { default: '' } }; },
  parseHTML() { return [{ tag: 'img[src]' }]; },
  renderHTML({ HTMLAttributes }) { return ['img', mergeAttributes(HTMLAttributes)]; },
  addCommands() {
    return { setImage: (attrs) => ({ commands }) => commands.insertContent({ type: 'image', attrs }) };
  },
});

const ToolButton = ({ label, active, disabled, onClick, children, testId }) => (
  <button
    type="button" onClick={onClick} disabled={disabled} aria-label={label} title={label}
    aria-pressed={active === undefined ? undefined : !!active}
    onMouseDown={(e) => e.preventDefault()}
    className={`min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg transition-colors disabled:opacity-35 ${
      active ? 'bg-primary text-white' : 'text-text-main hover:bg-primary/10'}`}
    data-testid={testId}
  >
    {children}
  </button>
);
const Divider = () => <span className="w-px self-stretch my-2 bg-primary/20" aria-hidden="true" />;

const normalizeUrl = (value) => {
  const v = value.trim();
  if (!v) return '';
  if (/^(https?:\/\/|mailto:)/i.test(v)) return v;
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return `mailto:${v}`;
  return `https://${v}`;
};

const EditorToolbar = ({ editor }) => {
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkValue, setLinkValue] = useState('');
  const linkInput = useRef(null);
  const fileInput = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [imageError, setImageError] = useState('');
  const [pendingImage, setPendingImage] = useState(null);
  const [altValue, setAltValue] = useState('');
  const [labelOpen, setLabelOpen] = useState(false);
  const [labelValue, setLabelValue] = useState('');
  useEffect(() => { if (linkOpen) linkInput.current?.focus(); }, [linkOpen]);
  if (!editor) return null;

  const pickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImageError('');
    setUploading(true);
    try {
      const prepared = await prepareImage(file);
      const form = new FormData();
      form.append('file', prepared.file);
      const res = await api.courseBuilderUpload(form);
      setPendingImage(res.data.url);
      setAltValue('');
    } catch (err) {
      setImageError(err?.response?.data?.detail || err?.message || 'The image could not be added.');
    } finally {
      setUploading(false);
    }
  };
  const placeImage = () => {
    editor.chain().focus().setImage({ src: pendingImage, alt: altValue.trim() }).run();
    setPendingImage(null);
  };
  const openBoxLabel = () => {
    if (!editor.isActive('callout')) { editor.chain().focus().toggleCallout('Reflection').run(); }
    setLabelValue(editor.getAttributes('callout').label || 'Reflection');
    setLabelOpen(true);
  };
  const applyBoxLabel = () => {
    editor.chain().focus().setCalloutLabel(labelValue.trim() || 'Reflection').run();
    setLabelOpen(false);
  };
  const run = (fn) => () => fn(editor.chain().focus()).run();
  const icon = 'w-[18px] h-[18px]';

  const openLink = () => { setLinkValue(editor.getAttributes('link').href || ''); setLinkOpen(true); };
  const applyLink = () => {
    const href = normalizeUrl(linkValue);
    if (href) editor.chain().focus().extendMarkRange('link').setLink({ href, target: '_blank', rel: 'noopener noreferrer' }).run();
    else editor.chain().focus().extendMarkRange('link').unsetLink().run();
    setLinkOpen(false);
  };

  return (
    <div className="border-b border-primary/15 bg-primary/[0.04]" data-testid="editor-toolbar">
      <div role="toolbar" aria-label="Text formatting" className="flex flex-wrap items-center gap-0.5 p-1.5">
        <ToolButton label="Normal text" active={editor.isActive('paragraph')} onClick={run((c) => c.setParagraph())} testId="toolbar-p"><Pilcrow className={icon} aria-hidden="true" /></ToolButton>
        <ToolButton label="Heading 1" active={editor.isActive('heading', { level: 1 })} onClick={run((c) => c.toggleHeading({ level: 1 }))} testId="toolbar-h1"><Heading1 className={icon} aria-hidden="true" /></ToolButton>
        <ToolButton label="Heading 2" active={editor.isActive('heading', { level: 2 })} onClick={run((c) => c.toggleHeading({ level: 2 }))} testId="toolbar-h2"><Heading2 className={icon} aria-hidden="true" /></ToolButton>
        <ToolButton label="Heading 3" active={editor.isActive('heading', { level: 3 })} onClick={run((c) => c.toggleHeading({ level: 3 }))} testId="toolbar-h3"><Heading3 className={icon} aria-hidden="true" /></ToolButton>
        <Divider />
        <ToolButton label="Bold" active={editor.isActive('bold')} onClick={run((c) => c.toggleBold())} testId="toolbar-bold"><Bold className={icon} aria-hidden="true" /></ToolButton>
        <ToolButton label="Italic" active={editor.isActive('italic')} onClick={run((c) => c.toggleItalic())} testId="toolbar-italic"><Italic className={icon} aria-hidden="true" /></ToolButton>
        <ToolButton label="Underline" active={editor.isActive('underline')} onClick={run((c) => c.toggleUnderline())} testId="toolbar-underline"><UnderlineIcon className={icon} aria-hidden="true" /></ToolButton>
        <Divider />
        <ToolButton label="Bulleted list" active={editor.isActive('bulletList')} onClick={run((c) => c.toggleBulletList())} testId="toolbar-bullet"><List className={icon} aria-hidden="true" /></ToolButton>
        <ToolButton label="Numbered list" active={editor.isActive('orderedList')} onClick={run((c) => c.toggleOrderedList())} testId="toolbar-ordered"><ListOrdered className={icon} aria-hidden="true" /></ToolButton>
        <ToolButton label="Quote" active={editor.isActive('blockquote')} onClick={run((c) => c.toggleBlockquote())} testId="toolbar-quote"><Quote className={icon} aria-hidden="true" /></ToolButton>
        <ToolButton label="Link" active={editor.isActive('link')} onClick={openLink} testId="toolbar-link"><Link2 className={icon} aria-hidden="true" /></ToolButton>
        <ToolButton label="Box (note or reflection)" active={editor.isActive('callout')} onClick={openBoxLabel} testId="toolbar-callout"><MessageSquareQuote className={icon} aria-hidden="true" /></ToolButton>
        <ToolButton label="Remove box" disabled={!editor.isActive('callout')} onClick={run((c) => c.lift('callout'))} testId="toolbar-callout-remove"><X className={icon} aria-hidden="true" /></ToolButton>
        <ToolButton label="Add image" disabled={uploading} onClick={() => fileInput.current?.click()} testId="toolbar-image">
          {uploading ? <Loader2 className={`${icon} animate-spin`} aria-hidden="true" /> : <ImagePlus className={icon} aria-hidden="true" />}
        </ToolButton>
        <ToolButton label="Divider line" onClick={run((c) => c.setHorizontalRule())} testId="toolbar-rule"><Minus className={icon} aria-hidden="true" /></ToolButton>
        <Divider />
        <ToolButton label="Clear formatting" onClick={run((c) => c.unsetAllMarks().clearNodes())} testId="toolbar-clear"><Eraser className={icon} aria-hidden="true" /></ToolButton>
        <ToolButton label="Undo" disabled={!editor.can().undo()} onClick={run((c) => c.undo())} testId="toolbar-undo"><Undo2 className={icon} aria-hidden="true" /></ToolButton>
        <ToolButton label="Redo" disabled={!editor.can().redo()} onClick={run((c) => c.redo())} testId="toolbar-redo"><Redo2 className={icon} aria-hidden="true" /></ToolButton>
      </div>
      {linkOpen && (
        <form
          onSubmit={(e) => { e.preventDefault(); applyLink(); }}
          onKeyDown={(e) => { if (e.key === 'Escape') { setLinkOpen(false); editor.commands.focus(); } }}
          className="flex items-center gap-2 px-2 pb-2" data-testid="link-form"
        >
          <label htmlFor="rte-link" className="sr-only">Link address</label>
          <input
            id="rte-link" ref={linkInput} value={linkValue} onChange={(e) => setLinkValue(e.target.value)}
            placeholder="Paste a web address, or leave empty to remove the link"
            className="flex-1 min-h-[44px] px-3 rounded-lg border border-primary/25 bg-background text-sm text-text-main focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
          <button type="submit" aria-label="Apply link" className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg bg-primary text-white"><Check className={icon} aria-hidden="true" /></button>
          <button type="button" aria-label="Cancel" onClick={() => { setLinkOpen(false); editor.commands.focus(); }} className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-text-main hover:bg-primary/10"><X className={icon} aria-hidden="true" /></button>
        </form>
      )}
      <input ref={fileInput} type="file" accept="image/*" className="sr-only" tabIndex={-1} aria-hidden="true" onChange={pickImage} data-testid="toolbar-image-input" />
      {imageError && <p role="alert" className="px-3 pb-2 text-sm text-red-600">{imageError}</p>}
      {pendingImage && (
        <form onSubmit={(e) => { e.preventDefault(); placeImage(); }} className="flex items-center gap-2 px-2 pb-2" data-testid="image-form">
          <img src={pendingImage} alt="" className="h-11 w-11 rounded-lg object-cover shrink-0" />
          <label htmlFor="rte-alt" className="sr-only">Describe the image</label>
          <input id="rte-alt" value={altValue} onChange={(e) => setAltValue(e.target.value)} autoFocus
            placeholder="Describe the image for people who can't see it"
            className="flex-1 min-h-[44px] px-3 rounded-lg border border-primary/25 bg-background text-sm text-text-main focus:outline-none focus:ring-2 focus:ring-primary/40" />
          <button type="submit" aria-label="Insert image" className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg bg-primary text-white"><Check className={icon} aria-hidden="true" /></button>
          <button type="button" aria-label="Cancel" onClick={() => setPendingImage(null)} className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-text-main hover:bg-primary/10"><X className={icon} aria-hidden="true" /></button>
        </form>
      )}
      {labelOpen && (
        <form onSubmit={(e) => { e.preventDefault(); applyBoxLabel(); }} onKeyDown={(e) => { if (e.key === 'Escape') setLabelOpen(false); }} className="flex items-center gap-2 px-2 pb-2" data-testid="box-label-form">
          <label htmlFor="rte-box-label" className="sr-only">Box title</label>
          <input id="rte-box-label" value={labelValue} onChange={(e) => setLabelValue(e.target.value)} onFocus={(e) => e.target.select()} autoFocus maxLength={60}
            placeholder="Box title, for example A note from me"
            className="flex-1 min-h-[44px] px-3 rounded-lg border border-primary/25 bg-background text-sm text-text-main focus:outline-none focus:ring-2 focus:ring-primary/40" />
          <button type="submit" aria-label="Apply box title" className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg bg-primary text-white"><Check className={icon} aria-hidden="true" /></button>
          <button type="button" aria-label="Cancel" onClick={() => setLabelOpen(false)} className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-text-main hover:bg-primary/10"><X className={icon} aria-hidden="true" /></button>
        </form>
      )}
    </div>
  );
};

/** Rich text editor for lesson text. Saves HTML. Shows text exactly as members will see it. */
const RichTextEditor = ({ content, onChange }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] }, link: { openOnClick: false, autolink: true } }),
      Callout,
      InlineImage,
      TableKit,
      Placeholder.configure({ placeholder: 'Start writing lesson content...' }),
    ],
    content: legacyToHtml(content),
    onUpdate: ({ editor: ed }) => onChange(ed.isEmpty ? '' : ed.getHTML()),
    editorProps: {
      attributes: {
        class: 'lesson-prose p-4 sm:p-5 min-h-[320px] max-h-[70vh] overflow-y-auto focus:outline-none',
        'aria-label': 'Lesson text',
        role: 'textbox',
        'aria-multiline': 'true',
      },
    },
  });

  useEffect(() => {
    if (!editor || content === undefined) return;
    const current = editor.isEmpty ? '' : editor.getHTML();
    if (current !== (content || '')) editor.commands.setContent(legacyToHtml(content), { emitUpdate: false });
  }, [content, editor]);

  return (
    <div className="border border-primary/25 rounded-xl overflow-hidden bg-background focus-within:ring-2 focus-within:ring-primary/30" data-testid="rich-text-editor">
      <EditorToolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichTextEditor;
